import { GraphQLClient } from "graphql-request"
import { ENV } from "~/ENV"

/** Supplies the current bearer token, or undefined when signed out. */
export type GetAccessToken = () => string | undefined

/** Called when the graph rejects our token, so the caller can refresh it. */
export type OnUnauthorized = () => void

const UNAUTHORIZED = 401

interface GraphQLClientOptions {
  readonly getAccessToken: GetAccessToken
  readonly onUnauthorized: OnUnauthorized
  readonly endpoint?: string
}

/**
 * `graphql-request` builds a `new URL(endpoint)` per request, which throws
 * `Failed to construct 'URL': Invalid URL` on a root-relative path — every query
 * on the page fails at once, with an error that names nothing recognisable.
 *
 * A relative endpoint is worth supporting rather than banning: it keeps requests
 * same-origin, which is how the POC facade avoids CORS and mixed content
 * (VITE_API_URL=/graphql, see POC.md), and it does not hard-code a dev port.
 */
const resolveEndpoint = (endpoint: string): string =>
  endpoint.startsWith("/") && typeof window !== "undefined"
    ? new URL(endpoint, window.location.origin).toString()
    : endpoint

/**
 * One client for the whole app. The Authorization header is attached here so no
 * caller ever sets it, matching how apps/mobile centralises it in its Apollo
 * auth link.
 */
export { resolveEndpoint }

export const createGraphQLClient = ({
  getAccessToken,
  onUnauthorized,
  endpoint = ENV.API_URL,
}: GraphQLClientOptions): GraphQLClient =>
  new GraphQLClient(resolveEndpoint(endpoint), {
    requestMiddleware: request => {
      const token = getAccessToken()

      return token ? { ...request, headers: { ...request.headers, Authorization: `Bearer ${token}` } } : request
    },
    responseMiddleware: response => {
      const status = response instanceof Error ? undefined : response.status

      if (status === UNAUTHORIZED) {
        onUnauthorized()
      }
    },
  })
